import { LearningAttempt } from '../models/learning.models';

interface DiagnosticAggregate {
  attempts: number;
  correct: number;
  accuracy: number | null;
}

interface QuestionDiagnostic extends DiagnosticAggregate {
  questionId: string;
  expectedAnswer: number;
  selectedAnswerCounts: Record<string, number>;
}

interface SkillDiagnostic extends DiagnosticAggregate {
  skillId: string;
  questions: QuestionDiagnostic[];
}

interface LearnerDiagnostic extends DiagnosticAggregate {
  alias: string;
  skills: SkillDiagnostic[];
}

export interface DiagnosticExport {
  schemaVersion: 1;
  generatedAt: string;
  privacy: {
    classification: 'pseudonymized';
    directIdentifiersIncluded: false;
    profileIdsIncluded: false;
    attemptIdsIncluded: false;
    exactAttemptTimestampsIncluded: false;
    learnerAliases: 'random-per-export';
    warning: string;
  };
  summary: DiagnosticAggregate & { learners: number; skills: number };
  skills: SkillDiagnostic[];
  learners: LearnerDiagnostic[];
}

export type DiagnosticExportIntegrityCode =
  | 'inconsistent-answer-key'
  | 'inconsistent-correctness'
  | 'invalid-grouping-key'
  | 'invalid-answer-index'
  | 'invalid-learner-alias'
  | 'duplicate-learner-alias'
  | 'invalid-generated-at';

export class DiagnosticExportIntegrityError extends Error {
  constructor(readonly code: DiagnosticExportIntegrityCode) {
    super('Diagnostic export data failed integrity checks.');
    this.name = 'DiagnosticExportIntegrityError';
  }
}

type AliasFactory = () => string;

function validAnswerIndex(value: number): boolean {
  return Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function validateGeneratedAt(value: string): void {
  const trimmed = value.trim();
  if (!trimmed) throw new DiagnosticExportIntegrityError('invalid-generated-at');
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString() !== trimmed) {
    throw new DiagnosticExportIntegrityError('invalid-generated-at');
  }
}

function validateAttempts(attempts: readonly LearningAttempt[]): void {
  for (const attempt of attempts) {
    if (!attempt.profileId.trim() || !attempt.skillId.trim() || !attempt.questionId.trim()) {
      throw new DiagnosticExportIntegrityError('invalid-grouping-key');
    }
    if (!validAnswerIndex(attempt.selectedAnswer) || !validAnswerIndex(attempt.expectedAnswer)) {
      throw new DiagnosticExportIntegrityError('invalid-answer-index');
    }
    if (attempt.correct !== (attempt.selectedAnswer === attempt.expectedAnswer)) {
      throw new DiagnosticExportIntegrityError('inconsistent-correctness');
    }
  }
}

function aggregate(attempts: readonly LearningAttempt[]): DiagnosticAggregate {
  const correct = attempts.filter((attempt) => attempt.correct).length;
  return {
    attempts: attempts.length,
    correct,
    accuracy: attempts.length ? Number((correct / attempts.length).toFixed(4)) : null
  };
}

function groupBy<T>(values: readonly T[], keyFor: (value: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const value of values) {
    const key = keyFor(value);
    const group = groups.get(key) ?? [];
    group.push(value);
    groups.set(key, group);
  }
  return groups;
}

function expectedAnswerForQuestion(questionId: string, attempts: readonly LearningAttempt[]): number {
  const expectedAnswers = new Set(attempts.map((attempt) => attempt.expectedAnswer));
  if (expectedAnswers.size !== 1) {
    void questionId;
    throw new DiagnosticExportIntegrityError('inconsistent-answer-key');
  }
  return attempts[0]!.expectedAnswer;
}

function buildQuestionDiagnostics(attempts: readonly LearningAttempt[]): QuestionDiagnostic[] {
  return [...groupBy(attempts, (attempt) => attempt.questionId).entries()]
    .map(([questionId, questionAttempts]) => {
      const selectedAnswerCounts: Record<string, number> = {};
      for (const attempt of questionAttempts) {
        const key = String(attempt.selectedAnswer);
        selectedAnswerCounts[key] = (selectedAnswerCounts[key] ?? 0) + 1;
      }
      return {
        questionId,
        expectedAnswer: expectedAnswerForQuestion(questionId, questionAttempts),
        ...aggregate(questionAttempts),
        selectedAnswerCounts
      };
    })
    .sort((a, b) => a.questionId.localeCompare(b.questionId));
}

function buildSkillDiagnostics(attempts: readonly LearningAttempt[]): SkillDiagnostic[] {
  return [...groupBy(attempts, (attempt) => attempt.skillId).entries()]
    .map(([skillId, skillAttempts]) => ({
      skillId,
      ...aggregate(skillAttempts),
      questions: buildQuestionDiagnostics(skillAttempts)
    }))
    .sort((a, b) => a.skillId.localeCompare(b.skillId));
}

function createLearnerAlias(aliasFactory: AliasFactory, usedAliases: Set<string>): string {
  const alias = aliasFactory().trim();
  if (!alias) throw new DiagnosticExportIntegrityError('invalid-learner-alias');
  if (usedAliases.has(alias)) throw new DiagnosticExportIntegrityError('duplicate-learner-alias');
  usedAliases.add(alias);
  return alias;
}

export function buildDiagnosticExport(
  attempts: readonly LearningAttempt[],
  aliasFactory: AliasFactory = () => `learner-${crypto.randomUUID().slice(0, 8)}`,
  generatedAt = new Date().toISOString()
): DiagnosticExport {
  validateGeneratedAt(generatedAt);
  validateAttempts(attempts);
  const learnerGroups = groupBy(attempts, (attempt) => attempt.profileId);
  const usedAliases = new Set<string>();
  const learners = [...learnerGroups.values()].map((learnerAttempts) => ({
    alias: createLearnerAlias(aliasFactory, usedAliases),
    ...aggregate(learnerAttempts),
    skills: buildSkillDiagnostics(learnerAttempts)
  }));
  const skills = buildSkillDiagnostics(attempts);

  return {
    schemaVersion: 1,
    generatedAt,
    privacy: {
      classification: 'pseudonymized',
      directIdentifiersIncluded: false,
      profileIdsIncluded: false,
      attemptIdsIncluded: false,
      exactAttemptTimestampsIncluded: false,
      learnerAliases: 'random-per-export',
      warning: 'This export is pseudonymized, not guaranteed anonymous. Learner grouping is retained for diagnostic comparison.'
    },
    summary: {
      ...aggregate(attempts),
      learners: learners.length,
      skills: skills.length
    },
    skills,
    learners
  };
}
